// src/components/Layout/Layout.tsx

import { Link, useLocation } from 'react-router-dom';
import { useStore } from '../../store/useStore';
import { useAuth } from '../../hooks/useAuth';
import { DarkModeToggle } from './DarkModeToggle';
import { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, UserCircleIcon, CreditCardIcon, Cog6ToothIcon, QuestionMarkCircleIcon, InformationCircleIcon, DocumentTextIcon, ShieldCheckIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { NotificationDropdown } from '../Notification/NotificationDropdown';

interface NavItem {
  path?: string;
  label: string;
  children?: { path: string; label: string }[];
}

interface UserMenuItem {
  label: string;
  path?: string;
  icon: React.ForwardRefExoticComponent<any>;
  onClick?: () => void;
}

interface LayoutProps {
  children: React.ReactNode;
  fullWidth?: boolean;
}

export const Layout: React.FC<LayoutProps> = ({ children, fullWidth = false }) => {
  const { user } = useStore();
  const { logout } = useAuth();
  const location = useLocation();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const userMenuItems: UserMenuItem[] = [
    { label: 'Profile', path: '/profile', icon: UserCircleIcon },
    { label: 'Billing', path: '/billing', icon: CreditCardIcon },
    { label: 'Settings', path: '/settings', icon: Cog6ToothIcon },
    { label: 'Terms & Conditions', path: '/terms', icon: DocumentTextIcon },
    { label: 'Privacy Policy', path: '/privacy', icon: ShieldCheckIcon },
    { label: 'Support', path: '/support', icon: QuestionMarkCircleIcon },
    { label: 'About Us', path: '/about', icon: InformationCircleIcon },
    { 
      label: 'Logout', 
      icon: ArrowRightOnRectangleIcon,
      onClick: logout
    }
  ];

  const navItems: NavItem[] = [
    { path: '/', label: 'Home' },
    {
      label: 'Overview',
      children: [
        { path: '/schedule', label: 'Weekly Schedule' },
        { path: '/schedule/month', label: 'Monthly Schedule' },
        { path: '/schedule/year', label: 'Yearly Schedule' },
        { path: '/schedule/archived-projects', label: 'Archived Projects' },
      ],
    },
    // { path: '/todo', label: 'Todo List' },
    { path: '/deadlines', label: 'Deadlines' },
    { path: '/projects', label: 'Projects' },
    { path: '/progress', label: 'Progress' },
    { path: '/tasks', label: 'Task Management' },
  ];

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      {user && (
        <nav className="bg-white dark:bg-gray-800 shadow-md flex-shrink-0 sticky top-0 z-50 mb-0 transition-colors">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex space-x-4">
                {navItems.map((item) =>
                  item.children ? (
                    <div
                      key={item.label}
                      className="relative"
                      ref={dropdownRef}
                    >
                      <button
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                        className={`inline-flex items-center px-3 py-2 border-b-2 text-sm font-medium transition-colors ${
                          item.children.some((child) => isActive(child.path))
                            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                        } focus:outline-none`}
                      >
                        {item.label}
                        <ChevronDownIcon className="ml-1 h-4 w-4" />
                      </button>
                      {dropdownOpen && (
                        <div className="absolute left-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-50">
                          <div className="py-1">
                            {item.children.map((child) => (
                              <Link
                                key={child.path}
                                to={child.path}
                                onClick={() => setDropdownOpen(false)}
                                className={`block px-4 py-2 text-sm ${
                                  isActive(child.path)
                                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                              >
                                {child.label}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      key={item.path}
                      to={item.path!}
                      className={`inline-flex items-center px-3 py-2 border-b-2 text-sm font-medium transition-colors ${
                        isActive(item.path!)
                          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      {item.label}
                    </Link>
                  )
                )}
              </div>
              <div className="flex items-center space-x-4">
                <NotificationDropdown />
                <DarkModeToggle />
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center space-x-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 focus:outline-none"
                    aria-expanded={userMenuOpen}
                    aria-haspopup="true"
                  >
                    <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white">
                      {user.email?.[0].toUpperCase()}
                    </div>
                    <span className="hidden md:block">{user.email}</span>
                    <ChevronDownIcon className="h-4 w-4" />
                  </button>

                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg z-50 ring-1 ring-black ring-opacity-5">
                      <div className="py-1" role="menu" aria-orientation="vertical">
                        {userMenuItems.map((item) => (
                          item.onClick ? (
                            <button
                              key={item.label}
                              onClick={item.onClick}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                              role="menuitem"
                            >
                              <item.icon className="h-5 w-5" />
                              <span>{item.label}</span>
                            </button>
                          ) : (
                            <Link
                              key={item.label}
                              to={item.path!}
                              className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                              role="menuitem"
                              onClick={() => setUserMenuOpen(false)}
                            >
                              <item.icon className="h-5 w-5" />
                              <span>{item.label}</span>
                            </Link>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </nav>
      )}

      {/* Main Content Area */}
      <div className="flex-grow overflow-hidden">
        <main
          className={`${
            fullWidth ? 'w-full px-4' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'
          } py-5 h-full`}
        >
          {children}
        </main>
      </div>
    </div>
  );
};
